import { IFormatNumber } from '../models/format-number';

const benchmarkPercentCalcs = {
  cdi: (assetValue: number, indexValue: number): string =>
    ((assetValue / indexValue) * 100).toFixed(0),
  ibov: (assetValue: number, indexValue: number): string =>
    (assetValue - indexValue).toFixed(2),
};

export const percentIndicator = (
  assetStats: number,
  benchmarkStats: number,
  benchmark: string
) => {
  if (!benchmarkStats || assetStats === undefined) {
    return '-';
  }

  return Number(benchmarkPercentCalcs[benchmark](assetStats, benchmarkStats));
};

export const formatCnpj = (value: number | string): string => {
  if (!value) {
    return '';
  }

  const cnpj = ('000000000' + value).substr(-14);

  return cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
};

const formatDecimalPlaces = (value: number, decimalPlaces: number = 2) => {
  return value
    .toFixed(decimalPlaces)
    .replace('.', ',')
    .replace(/(\d)(?=(\d{3})+,)/g, '$1.')
    .replace(',00', '');
};

const formatThousandMask = (value: number): string => {
  const absoluteNumber = Math.abs(value);

  if (absoluteNumber >= 1e3 && absoluteNumber < 1e6)
    return `${formatDecimalPlaces(value / 1e3)} mil`;

  if (absoluteNumber >= 1e6 && absoluteNumber < 1e9)
    return `${formatDecimalPlaces(value / 1e6)} mi`;

  if (absoluteNumber >= 1e9 && absoluteNumber < 1e12)
    return `${formatDecimalPlaces(value / 1e9)} bi`;

  if (absoluteNumber >= 1e12) return `${formatDecimalPlaces(value / 1e12)} tri`;

  return `${formatDecimalPlaces(value)}`;
};

export const formatNumber = (
  value: number | string,
  { type, thousandMask, decimalPlaces = 2 }: IFormatNumber = {}
) => {
  const nullPlaceholder = '-';

  if (value === null || value === undefined || isNaN(Number(value))) {
    return nullPlaceholder;
  }

  const numberValue = Number(value);
  const formattedValue = formatDecimalPlaces(numberValue, decimalPlaces);

  if (type && type === 'percent') {
    return `${formattedValue}%`;
  }

  if (type && type === 'money' && thousandMask) {
    const valueWithThousand = formatThousandMask(numberValue);
    return `R$ ${valueWithThousand}`;
  }

  if (type && type === 'money') {
    const valueWithThousand = formatDecimalPlaces(numberValue);
    return `R$ ${valueWithThousand}`;
  }

  if (thousandMask) {
    return formatThousandMask(numberValue);
  }

  return formattedValue;
};

export const ASSET_TYPE_NAME = {
  idx: 'indice',
  fi: 'fundo',
  fund: 'fundo',
  b3: 'ação',
};

export const mapOrder = (array, order, key) => {
  array.sort((a, b) => {
    const A = a[key];
    const B = b[key];
    return order.indexOf(A) > order.indexOf(B) ? 1 : -1;
  });
  return array;
};

export const convertCentsInReals = (numberValue: number) => {
  const valueWithThousand = numberValue / 100;
  return valueWithThousand.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};

export const getFirstLetterOfString = (text: string): string | null => {
  return text?.slice(0, 1) || null;
};
